import { identity } from 'ramda'
import { useMemo } from 'react'
import { useQueryClient } from 'react-query'

import { useCustomers } from '~shared-components/hooks/useCustomers'
import { loadSelectedSalesGroupId } from '~shared-components/redux/localStorage'
import { deserialize } from '~shared-components/utils/deserialize'
import { ifArrayReturnNthItem } from '~shared-components/utils/functional'

import { GetMeResponse } from '../api/users'
import {
  ApplicationUser,
  convertToCustomerContext,
  convertTransformedUserToApplicationUser,
  CustomerContext,
  ExpandedUserResource,
  transformApiUserToUser,
  TransformedUser,
  UserPermission,
} from '../transforms/user.transforms'
import { QueryCacheKey } from '../utils/enums'
import { useSalesGroups } from './useSalesGroups'

export interface LoggedInUserResponse {
  isFetching: boolean
  isLoading: boolean
  user: ApplicationUser | undefined
}

export const SharedQueryOptions = {
  cacheTime: 60 * 60 * 1000,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  staleTime: 60 * 60 * 1000,
}

const emptyPermissions: UserPermission[] = []

export const useLoggedInUser = (): LoggedInUserResponse => {
  const queryClient = useQueryClient()

  const userQuery = queryClient.getQueryState<GetMeResponse>([
    QueryCacheKey.User,
    'me',
  ])
  const userModel = userQuery?.data?.me
  const transformedUser: TransformedUser | undefined = useMemo(
    () =>
      userModel &&
      transformApiUserToUser(deserialize<ExpandedUserResource>(userModel)),
    [userModel],
  )
  const userPermissions = transformedUser?.permissions ?? emptyPermissions

  const customerContextIds = useMemo(
    () =>
      userPermissions
        .map(permission => permission.customerContextId)
        .filter(identity) as string[],

    [userPermissions],
  )

  const permisionsSalesGroupIds = useMemo(
    () =>
      userPermissions
        .map(permission => permission.salesGroupId)
        .filter(identity) as string[],

    [userPermissions],
  )

  const {
    data: customerModels,
    isFetching: isFetchingCustomers,
    isLoading: isLoadingCustomers,
  } = useCustomers({
    customerContextIds,
    isEnabled: transformedUser !== undefined,
  })

  const customers: CustomerContext[] | undefined = useMemo(
    () =>
      customerModels && customerModels.length >= 1
        ? convertToCustomerContext(userPermissions, customerModels)
        : undefined,
    [customerModels, userPermissions],
  )

  const currentSalesGroupId = useMemo(() => {
    const salesGroupIdFromStorage = loadSelectedSalesGroupId(
      transformedUser?.email,
    )

    return (
      userPermissions?.find(p => p?.salesGroupId === salesGroupIdFromStorage)
        ?.salesGroupId || ifArrayReturnNthItem(userPermissions)?.salesGroupId
    )
  }, [userPermissions, transformedUser])

  const salesGroupIds =
    customers && customers.length > 0
      ? customers?.map(c => c.salesGroup.id)
      : permisionsSalesGroupIds

  const {
    data: salesGroups,
    isFetching: isFetchingSalesGroups,
    isLoading: isLoadingSalesGroups,
  } = useSalesGroups({
    ids: salesGroupIds, // if no ids found, it must be SuperAdmin - get all salesgroups
    isEnabled: transformedUser !== undefined,
  })

  return useMemo(() => {
    const isLoading = isLoadingCustomers || isLoadingSalesGroups
    const isFetching =
      userQuery?.isFetching || isFetchingCustomers || isFetchingSalesGroups

    let user: ApplicationUser | undefined
    if (transformedUser && !isLoading) {
      user = convertTransformedUserToApplicationUser(
        transformedUser,
        customers,
        salesGroups,
        currentSalesGroupId,
      )
    }
    return {
      isFetching,
      isLoading,
      user,
    }
  }, [
    customers,
    currentSalesGroupId,
    isFetchingCustomers,
    isLoadingCustomers,
    salesGroups,
    isFetchingSalesGroups,
    isLoadingSalesGroups,
    userQuery?.isFetching,
    transformedUser,
  ])
}
